import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import type {Pagination} from 'lib/Listings';
import {GLOBAL_SEARCH_URL} from 'search/SearchConstants';
import * as net from 'util/Net';

import {STATUS_CODE_TO_STATUS} from '../runs/PredictionBatchTypes';
import type {SortDirection} from './listingTypes';
import type {JobStatus} from './StatusBadge';

export interface BatchRow {
  createdAt: string;
  id: string;
  modelName: string;
  modifiedAt: string;
  name: string;
  status: JobStatus;
}

type BatchSortKey = 'createdAt' | 'modifiedAt' | 'name';

interface UseBatchesSearchParams {
  limit: number;
  offset: number;
  query: string;
  sort: {direction: SortDirection; key: BatchSortKey};
}

export interface UseBatchesSearchResult {
  batches: Array<BatchRow>;
  error: string | null;
  isLoading: boolean;
  pagination: Pagination;
  refresh: () => void;
}

const TERMINAL_STATUSES: ReadonlySet<JobStatus> = new Set(['SUCCEEDED', 'FAILED']);
const POLL_INTERVAL_MS = 30_000;

const SORT_KEY_MAP: Record<BatchSortKey, string> = {
  createdAt: 'created_at',
  modifiedAt: 'modified_at',
  name: 'name',
};

interface PredictionBatchListingData {
  createdAt: string;
  creator?: {name: string} | null;
  id: string;
  modelName: string;
  modifiedAt: string;
  name: string;
  statusCode: string;
}

interface SearchResponse {
  ids: Array<string>;
  isExact: boolean;
  predictionBatchesById?: Record<string, PredictionBatchListingData>;
  total: number;
}

function mapToBatchRow(id: string, listing: PredictionBatchListingData | undefined): BatchRow {
  if (!listing) {
    return {id, name: id, status: 'PENDING', modelName: '', createdAt: '', modifiedAt: ''};
  }
  return {
    createdAt: listing.createdAt ?? '',
    id: listing.id,
    modelName: listing.modelName ?? '',
    modifiedAt: listing.modifiedAt ?? '',
    name: listing.name ?? id,
    status: STATUS_CODE_TO_STATUS[listing.statusCode] ?? 'PENDING',
  };
}

export default function useBatchesSearch({
  query,
  sort,
  offset,
  limit,
}: UseBatchesSearchParams): UseBatchesSearchResult {
  const [batches, setBatches] = React.useState<Array<BatchRow>>([]);
  const [pagination, setPagination] = React.useState<Pagination>({offset: 0, limit, total: 0});
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [refreshCounter, setRefreshCounter] = React.useState(0);
  const hasActiveBatchesRef = React.useRef(false);

  const refresh = React.useCallback(() => {
    setRefreshCounter((prev) => prev + 1);
  }, []);

  // Polling: only when there are non-terminal batches and page is visible
  React.useEffect(() => {
    if (!hasActiveBatchesRef.current) {
      return;
    }

    let intervalId: ReturnType<typeof setInterval> | null = null;

    function startPolling(): void {
      if (intervalId == null) {
        intervalId = setInterval(() => {
          setRefreshCounter((prev) => prev + 1);
        }, POLL_INTERVAL_MS);
      }
    }

    function stopPolling(): void {
      if (intervalId != null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    }

    function handleVisibilityChange(): void {
      if (document.visibilityState === 'hidden') {
        stopPolling();
      } else {
        startPolling();
      }
    }

    if (document.visibilityState !== 'hidden') {
      startPolling();
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [batches]);

  // Fetch data
  React.useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    const searchParams = {
      filters: [{key: 'TYPES', operator: 'IS_ONE_OF', value: [ModelTypeConstants.PREDICTION_BATCH]}],
      limit,
      offset,
      query,
      sorts: [{key: SORT_KEY_MAP[sort.key], reverse: sort.direction === 'desc'}],
    };

    net
      .postJSONPromise({url: GLOBAL_SEARCH_URL, data: searchParams})
      .then((response: SearchResponse) => {
        if (cancelled) {
          return;
        }
        const predictionBatchesById = response.predictionBatchesById ?? {};
        const rows = response.ids.map((id) => mapToBatchRow(id, predictionBatchesById[id]));
        // Set the active-batches ref before setBatches so the polling effect (keyed on `batches`) reads
        // the up-to-date value when it re-runs; otherwise polling never starts after the first load.
        hasActiveBatchesRef.current = rows.some((row) => !TERMINAL_STATUSES.has(row.status));
        setBatches(rows);
        setPagination({offset, limit, total: response.total, isExact: response.isExact});
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        setError(err instanceof Error ? err.message : 'Search failed');
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [query, sort.key, sort.direction, offset, limit, refreshCounter]);

  return {batches, pagination, isLoading, error, refresh};
}
